import {
	CdkDragDrop,
	moveItemInArray,
	transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
} from '@angular/core';
import { openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { ChecklistItem } from '@models/checklist';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { HttpHandlerService } from '@services/httpHandler/httpHandler.service';
import {
	MatterChecklistsDispatch,
	MatterChecklistsState,
} from '@state/matter/checklist';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UserProfileState } from '@state/user-profile';
import { nanoid } from 'nanoid/non-secure';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { TestHooks } from './matter-checklist-stage.hooks';

@UntilDestroy()
@Component({
	selector: 'app-matter-checklist-stage',
	templateUrl: './matter-checklist-stage.component.html',
	styleUrls: ['./matter-checklist-stage.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [openClose()],
	standalone: false,
})
export class MatterChecklistStageComponent implements OnInit {
	testHooks = TestHooks;
	@Input() stage: ChecklistItem;
	@Input() location: 'overview' | 'nav';

	@ViewSelectSnapshot(UserProfileState.getCurrentBusinessId) businessId: string;
	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(MatterChecklistsState.getTasksByStageId) tasksFilterFn$: Observable<
		(id: string) => ChecklistItem[]
	>;
	@Select(MatterChecklistsState.getStageCompletePercentage)
	completePercentageFilterFn$: Observable<(id: string) => string>;
	@SelectSnapshot(MatterChecklistsState.getAll) checklistItems: ChecklistItem[];
	@SelectSnapshot(MatterChecklistsState.getStages) stages: ChecklistItem[];

	public ICON = ICON;
	public isOpened = false;
	public newTask = '';
	public addingTask = false;
	private tasksSnapshot: ChecklistItem[] = [];
	private tasksSubject = new BehaviorSubject<ChecklistItem[]>(
		this.tasksSnapshot,
	);
	public tasks$ = this.tasksSubject.asObservable();
	public completedPercentage$: Observable<string>;

	constructor(
		private checklistDispatch: MatterChecklistsDispatch,
		private http: HttpHandlerService,
		private analytics: AnalyticsService,
	) {}

	ngOnInit(): void {
		this.getTasks$(this.stage.id)
			.pipe(
				untilDestroyed(this),
				map((tasks) => this.sortChecklistItems(tasks)),
			)
			.subscribe((sortedTasks) => {
				this.tasksSnapshot = sortedTasks;
				this.tasksSubject.next(sortedTasks);
			});

		this.completedPercentage$ = this.getCompletePercentage$(this.stage.id).pipe(
			untilDestroyed(this),
		);

		this.isOpened = !!this.stage.opened;
	}

	toggleStage(): void {
		this.isOpened = !this.isOpened;
		this.checklistDispatch.updateStageOpenedState({
			id: this.stage.id,
			opened: this.isOpened,
		});
	}

	toggleChecklistTask(id: string, value: boolean): void {
		const updateChecklistPath = `checklist/matters/${this.matterId}/matterChecklistItems/${id}/update`;
		const body = {
			value,
		};

		this.http
			.patch(updateChecklistPath, body)
			.pipe(untilDestroyed(this), take(1))
			.subscribe();
	}

	saveNewTask(): void {
		const currentLastTask = this.checklistItems.find(
			(ci) =>
				ci.type === 'task' &&
				ci.parentId === this.stage.id &&
				(ci.nextNodeId === '' ||
					!this.checklistItems.find((c) => c.id === ci.nextNodeId)),
		);
		const newTaskId = nanoid(8);

		const newTask: ChecklistItem = {
			id: newTaskId,
			businessId: this.stage.businessId,
			type: 'task',
			title: this.newTask,
			matterId: this.stage.matterId,
			parentId: this.stage.id,
			complete: false,
			created: new Date().toISOString(),
			lastEdited: new Date().toISOString(),
			headNode: !currentLastTask,
			nextNodeId: '',
		};

		this.checklistDispatch.CreateItem(newTask);

		const secondLastTask: ChecklistItem = {
			...currentLastTask,
			nextNodeId: newTaskId,
		};
		this.checklistDispatch.Update(secondLastTask);

		this.newTask = '';
		this.addingTask = false;
	}

	addTask() {
		this.analytics.logEvent(AnalyticsEvent.CHECKLIST_ADD_TASK);
		this.addingTask = true;
	}

	getTasks$(id: string): Observable<ChecklistItem[]> {
		return this.tasksFilterFn$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(id)),
		);
	}

	getCompletePercentage$(id: string): Observable<string> {
		return this.completePercentageFilterFn$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(id)),
		);
	}

	private getChecklistItemsToUpdate = (
		checklistItems: ChecklistItem[],
		targetParentId: string = null,
	): ChecklistItem[] => {
		const checklistItemsToUpdate: ChecklistItem[] = [];
		const lastIndex = checklistItems.length - 1;

		checklistItems.forEach((currentItem, index) => {
			let itemRequiresUpdate = false;

			if (index === 0 && !currentItem.headNode) {
				currentItem.headNode = true;
				itemRequiresUpdate = true;
			}

			if (index > 0 && !!currentItem.headNode) {
				currentItem.headNode = false;
				itemRequiresUpdate = true;
			}

			if (index === lastIndex && currentItem.nextNodeId !== '') {
				currentItem.nextNodeId = '';
				itemRequiresUpdate = true;
			}

			const nextItemId = checklistItems.at(index + 1)?.id;
			if (index < lastIndex && currentItem.nextNodeId !== nextItemId) {
				currentItem.nextNodeId = nextItemId;
				itemRequiresUpdate = true;
			}

			if (targetParentId && currentItem.parentId !== targetParentId) {
				currentItem.parentId = targetParentId;
				itemRequiresUpdate = true;
			}

			if (itemRequiresUpdate) {
				checklistItemsToUpdate.push(currentItem);
			}
		});

		return checklistItemsToUpdate;
	};

	sortChecklistItems(unsortedItems: ChecklistItem[]) {
		if (unsortedItems.length === 0) return unsortedItems;

		const checklistItems = structuredClone(unsortedItems);
		const firstItemIndex = checklistItems.findIndex((s) => s.headNode);

		if (firstItemIndex === -1) return checklistItems;

		const sortedItems = checklistItems.splice(firstItemIndex, 1);

		while (checklistItems.length > 0) {
			const nextItemId = sortedItems.at(-1).nextNodeId;

			/**
			 * Concat is necessary in event that tree traversal is broken. This would cause some items to be orphaned.
			 * Example a -> b -> c -> d, if b -> c is broken, c and d would be orphaned. This would prevent that.
			 */
			if (!nextItemId) return sortedItems.concat(checklistItems);

			const nextItemIndex = checklistItems.findIndex(
				(s) => s.id === nextItemId,
			);
			const nextItem = checklistItems.splice(nextItemIndex, 1).at(0);

			sortedItems.push(nextItem);
		}

		return sortedItems;
	}

	dropTask(event: CdkDragDrop<ChecklistItem[]>) {
		const previousTaskIndex = event.previousIndex;
		const currentTaskIndex = event.currentIndex;
		const previousStageId = this.getStageIdFromElementId(
			event.previousContainer.id,
		);
		const currentStageId = this.getStageIdFromElementId(event.container.id);

		if (
			previousStageId === currentStageId &&
			previousTaskIndex === currentTaskIndex
		)
			return;

		let tasksToUpdate: ChecklistItem[];

		// Moving task within the same stage
		if (previousStageId === currentStageId) {
			const tasks = structuredClone(this.tasksSnapshot);
			moveItemInArray(tasks, previousTaskIndex, currentTaskIndex);
			tasksToUpdate = this.getChecklistItemsToUpdate(tasks);
			this.tasksSnapshot = tasks; // Update local snapshot
		}

		// Moving task between different stages
		else {
			const previousTasks = this.sortChecklistItems(
				structuredClone(
					this.checklistItems.filter(
						(t) => t.type === 'task' && t.parentId === previousStageId,
					),
				),
			);
			const currentTasks = structuredClone(this.tasksSnapshot);
			transferArrayItem(
				previousTasks,
				currentTasks,
				previousTaskIndex,
				currentTaskIndex,
			);

			tasksToUpdate = [
				...this.getChecklistItemsToUpdate(previousTasks, previousStageId),
				...this.getChecklistItemsToUpdate(currentTasks, currentStageId),
			];

			this.tasksSnapshot = currentTasks;
		}

		this.tasksSubject.next(this.tasksSnapshot);

		tasksToUpdate.forEach((task) => {
			this.checklistDispatch.Update(task);
		});
	}

	trackByFunction(index: number, item: ChecklistItem): string {
		return `${item.id}-${item.nextNodeId}`;
	}

	getStageElementId(stage: ChecklistItem) {
		return `${stage.id}-${this.location}`;
	}

	getStageIdFromElementId(elementId: string) {
		return elementId.replace(`-${this.location}`, '');
	}

	getConnectedStages(stage: ChecklistItem) {
		const connectedStages = this.stages.reduce((acc, s) => {
			if (s.id !== stage.id) {
				acc.push(this.getStageElementId(s));
			}

			return acc;
		}, [] as string[]);
		return connectedStages;
	}

	deleteStage(stageId: string) {
		const remainingStages = this.sortChecklistItems(this.stages).filter(
			(s) => s.id !== stageId,
		);
		if (remainingStages.length === 0) {
			this.checklistDispatch.RemoveAll();

			return;
		}
		const stagesToUpdate = this.getChecklistItemsToUpdate(remainingStages);
		const checklistItemIdsToDelete = this.checklistItems.reduce((acc, ci) => {
			if (
				(ci.type === 'stage' && ci.id === stageId) ||
				(ci.type === 'task' && ci.parentId === stageId)
			) {
				acc.push(ci.id);
			}

			return acc;
		}, [] as string[]);

		stagesToUpdate.forEach((s) => {
			this.checklistDispatch.Update(s);
		});

		checklistItemIdsToDelete.forEach((itemId) => {
			this.checklistDispatch.Remove(itemId);
		});
	}

	deleteTask(taskId: string) {
		const sortedTasks = this.sortChecklistItems(
			this.checklistItems.filter(
				(ci) => ci.parentId === this.stage.id && ci.type === 'task',
			),
		);
		const remainingTasks = sortedTasks.filter((t) => t.id !== taskId);
		const tasksToUpdate = this.getChecklistItemsToUpdate(remainingTasks);

		tasksToUpdate.forEach((t) => {
			this.checklistDispatch.Update(t);
		});

		this.checklistDispatch.Remove(taskId);
	}

	addTaskInputBlur() {
		this.addingTask = false;
	}
}
